import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Clock, Play, Square, Plus } from "lucide-react";
import { billedHoursFromSeconds } from "@/lib/labels";
import { toast } from "sonner";

const LS_KEY = "murphy_dash_timer";
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function DashboardTimer() {
  const [customers, setCustomers] = useState([]);
  const [recent, setRecent] = useState([]);
  const [activities, setActivities] = useState([]);
  const [startTs, setStartTs] = useState(() => {
    const v = localStorage.getItem(LS_KEY);
    return v ? Number(v) : null;
  });
  const [elapsed, setElapsed] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ customer_id: "", activity: "", description: "", billable: true, hours: "" });

  const loadRecent = () => api.get("/time-entries/recent-customers?limit=20").then((r) => setRecent(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/activity-types").then((r) => setActivities(r.data)).catch(() => {});
    loadRecent();
  }, []);

  const running = startTs != null;
  useEffect(() => {
    if (!running) { setElapsed(0); return; }
    const tick = () => setElapsed(Math.floor((Date.now() - startTs) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [running, startTs]);

  const start = () => { const t = Date.now(); localStorage.setItem(LS_KEY, String(t)); setStartTs(t); };
  const stop = () => {
    const secs = startTs ? (Date.now() - startTs) / 1000 : 0;
    const h = billedHoursFromSeconds(secs);
    localStorage.removeItem(LS_KEY);
    setStartTs(null);
    setF({ customer_id: "", activity: "", description: "", billable: true, hours: String(h) });
    setDialogOpen(true);
  };

  const fmt = (s) => {
    const hh = String(Math.floor(s / 3600)).padStart(2, "0");
    const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  };

  const recentIds = new Set(recent.map((r) => r.customer_id));
  const others = customers.filter((c) => !recentIds.has(c.id));

  const save = async () => {
    if (!f.hours || Number(f.hours) <= 0) { toast.error("Angi antall timer"); return; }
    setSaving(true);
    try {
      const cust = customers.find((c) => c.id === f.customer_id);
      const rec = recent.find((r) => r.customer_id === f.customer_id);
      await api.post("/time-entries", {
        customer_id: f.customer_id || null,
        customer_name: cust ? cust.legal_name : (rec ? rec.customer_name : null),
        activity: f.activity || null, date: todayISO(), hours: Number(f.hours),
        description: f.description, billable: f.billable,
      });
      toast.success("Timeføring lagret");
      setDialogOpen(false);
      loadRecent();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="rounded-xl border border-border bg-card p-5 flex items-center gap-4 mb-4" data-testid="dashboard-timer">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${running ? "bg-emerald-500/15" : "bg-secondary"}`}>
          <Clock className={`w-5 h-5 ${running ? "text-emerald-400" : "text-muted-foreground"}`} strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="label-caps">Stoppeklokke {running ? "· pågår" : ""}</p>
          <p className="font-display text-2xl font-medium tabular-nums" data-testid="dashboard-timer-display">{fmt(elapsed)}</p>
        </div>
        {running ? (
          <Button onClick={stop} className="bg-white text-black hover:bg-white/90" data-testid="dashboard-timer-stop"><Square className="w-4 h-4 mr-1.5" /> Stopp &amp; før</Button>
        ) : (
          <Button onClick={start} variant="secondary" data-testid="dashboard-timer-start"><Play className="w-4 h-4 mr-1.5" /> Start timer</Button>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-popover border-border" data-testid="dashboard-timer-dialog">
          <DialogHeader>
            <DialogTitle>Før tid ({f.hours} t)</DialogTitle>
            <DialogDescription className="sr-only">Fyll inn kunde, aktivitet og timer for den målte tiden.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-caps">Kunde</Label>
              <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v })}>
                <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="timer-customer-select"><SelectValue placeholder="Velg kunde (valgfritt)" /></SelectTrigger>
                <SelectContent className="bg-popover border-border max-h-72">
                  {recent.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Nylige</SelectLabel>
                      {recent.map((r) => <SelectItem key={`r-${r.customer_id}`} value={r.customer_id}>{r.customer_name || "Kunde"}</SelectItem>)}
                    </SelectGroup>
                  )}
                  <SelectGroup>
                    <SelectLabel>Alle kunder</SelectLabel>
                    {others.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps">Aktivitet</Label>
              <Select value={f.activity} onValueChange={(v) => setF({ ...f, activity: v })}>
                <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="timer-activity-select"><SelectValue placeholder="Velg aktivitet (valgfritt)" /></SelectTrigger>
                <SelectContent className="bg-popover border-border max-h-72">
                  {activities.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-caps">Timer</Label><Input type="number" step="0.25" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="timer-hours" /></div>
              <div className="flex items-end"><label className="flex items-center gap-2 text-sm pb-2.5"><Checkbox checked={f.billable} onCheckedChange={(v) => setF({ ...f, billable: v })} data-testid="timer-billable" /> Fakturerbart</label></div>
            </div>
            <div><Label className="label-caps">Beskrivelse</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="timer-desc" placeholder="Hva jobbet du med?" /></div>
            <Button onClick={save} disabled={saving} className="w-full bg-white text-black hover:bg-white/90" data-testid="timer-save-btn"><Plus className="w-4 h-4 mr-1" /> {saving ? "Lagrer…" : "Lagre timeføring"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
